import { useWatch } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';
import PropTypes from 'prop-types';
import { Autocomplete, Chip, TextField } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useState, useEffect } from 'react';

const FormSelectMultiple = ({ options, existingYhteystiedot, onUpdate, fieldName, ...otherProps }) => {
    const [selectedOptions, setSelectedOptions] = useState([]);
    const standalone = otherProps.standalone || (Array.isArray(existingYhteystiedot) && typeof onUpdate === 'function');

    const formValues = useWatch({
        name: fieldName,
        defaultValue: null
    });

    const formContext = useFormContext();
    const control = formContext?.control;
    const errors = formContext?.formState?.errors ?? {};

    // Update selectedOptions when formValues or existingYhteystiedot change
    useEffect(() => {
        if (existingYhteystiedot) {
            setSelectedOptions(existingYhteystiedot);
        } else if (formValues) {
            setSelectedOptions(formValues);
        }
    }, [formValues, existingYhteystiedot]);

    const handleSelectChange = (event, selectedItems) => {
        setSelectedOptions(selectedItems);
        onUpdate && onUpdate(selectedItems); // Call the onUpdate callback to update the parent component's state
    };

    const handleCombinedChange = (event, selectedItems, field) => {
        field.onChange(selectedItems); // Call the default onChange provided by RHF
        handleSelectChange(event, selectedItems); // Call your custom onChange function
    };

    if (standalone || !control) {
        return (
            <Autocomplete
                multiple
                fullWidth
                autoHighlight
                value={selectedOptions}
                options={options}
                onChange={handleSelectChange}
                renderInput={(params) =>
                    <TextField
                        { ...params }
                        { ...otherProps }
                        variant='outlined'
                        error={Boolean(otherProps.error)}
                        helperText={otherProps.helperText ?? ' '}
                    />
                }
                renderTags={(tags, getTagProps) =>
                    tags.map((option, i) =>
                        <Chip
                            { ...getTagProps({ i }) }
                            key={option}
                            label={option}
                            variant='outlined'
                            deleteIcon={<ClearIcon sx={{ color: 'taphaBlue.main' }} />}
                            onDelete={() => {
                                const updatedOptions = selectedOptions.filter((item) => item !== option);
                                setSelectedOptions(updatedOptions);
                                onUpdate && onUpdate(updatedOptions);
                            }}
                        />
                    )
                }
            />
        );
    }

    return (
        <Controller
            name={fieldName}
            control={control}
            render={({ field }) =>
                <Autocomplete
                    multiple
                    fullWidth
                    autoHighlight
                    value={selectedOptions}
                    options={options}
                    onChange={(event, selectedItems) => handleCombinedChange(event, selectedItems, field)}
                    renderInput={(params) =>
                        <TextField
                            { ...params }
                            { ...otherProps }
                            variant='outlined'
                            error={!!errors[fieldName]}
                            helperText={errors?.[fieldName]?.message ?? ' '}
                        />
                    }
                    renderTags={(tags, getTagProps) =>
                        tags.map((option, i) =>
                            <Chip
                                { ...getTagProps({ i }) }
                                key={option}
                                label={option}
                                variant='outlined'
                                deleteIcon={<ClearIcon sx={{ color: 'taphaBlue.main' }} />}
                                onDelete={() => {
                                    const updatedOptions = selectedOptions.filter((item) => item !== option);
                                    setSelectedOptions(updatedOptions);
                                    onUpdate && onUpdate(updatedOptions); // Update the parent with the new list
                                }}
                            />
                        )
                    }
                />
            }
        />
    );
};

FormSelectMultiple.propTypes = {
    values: PropTypes.array,
    name: PropTypes.string,
    onUpdate: PropTypes.func,
    existingYhteystiedot: PropTypes.array,
    fieldName: PropTypes.string.isRequired,
    standalone: PropTypes.bool
};

export default FormSelectMultiple;
